import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { debounceTime, distinctUntilChanged, Subject, takeUntil } from 'rxjs';
import { ICommissionNegotiation, INewCommissionDTO } from 'src/app/features/agents/models/commissions.models';
import { rangeValidator } from 'src/app/shared/validators/fields-validators';

@Component({
  selector: 'app-new-commission-negotiation',
  templateUrl: './new-commission-negotiation.component.html',
  styleUrls: ['./new-commission-negotiation.component.scss'],
})
export class NewCommissionNegotiationComponent implements OnInit {
  @Input() commission!: INewCommissionDTO;
  @Output() nextStep = new EventEmitter<Partial<INewCommissionDTO>>();
  @Output() previousStep = new EventEmitter<Partial<INewCommissionDTO>>();

  @ViewChild('negotiationsArray') negotiationsArray!: ElementRef;

  negotiationForm!: FormGroup;
  negotiationFormArray!: FormArray<any>;

  destroy$ = new Subject<void>();

  constructor() {}

  ngOnInit(): void {
    this.negotiationFormArray = new FormArray([]) as any;

    this.negotiationForm = new FormGroup({
      negotiations: this.negotiationFormArray,
    });

    this.negotiationFormArray.valueChanges.pipe(debounceTime(500), takeUntil(this.destroy$), distinctUntilChanged()).subscribe((values) => {
      this.updateNegotiationValidators();
    });

    if(this.commission?.negotiations?.length) {
      this.commission?.negotiations.map((negotiation: ICommissionNegotiation) => this.addNegotiation(negotiation.min_extra_discount, negotiation.max_extra_discount, negotiation.new_commission))
    }
  }

  get negotiations(): FormArray {
    return this.negotiationForm.get('negotiations') as FormArray;
  }

  get lastNegotationMaxValue(): number {
    return this.negotiationFormArray?.controls[this.negotiationFormArray.length - 1]?.get('max')?.value || 0;
  }

  private updateNegotiationValidators(): void {
    this.negotiationFormArray.controls.forEach((control, index) => {
      const currentThreshold = this.negotiationFormArray.controls[index];
      const minCurrentThreshold = currentThreshold?.get('min_extra_discount')?.value || 0;
      if (index !== 0) {
        const previousThreshold = this.negotiationFormArray.controls[index - 1];
        const maxPreviousThreshold = previousThreshold.get('max_extra_discount')?.value;

        if (maxPreviousThreshold !== undefined && maxPreviousThreshold !== null) {
          control.get('min_extra_discount')?.setValidators([
            Validators.required,
            Validators.min(maxPreviousThreshold > 0 ? parseFloat(parseFloat(maxPreviousThreshold + 0.01).toFixed(2)) : 0),
            Validators.max(100)
          ]);
          control.get('min_extra_discount')?.updateValueAndValidity();

          const maxValidators = [
            Validators.min(minCurrentThreshold > 0 ? parseFloat(parseFloat(minCurrentThreshold + 0.01).toFixed(2)) : 0),
            Validators.max(100),
          ];
          if(index !== this.negotiationFormArray.length - 1)
          {
            maxValidators.push(Validators.required);
          }
          control.get('max_extra_discount')?.setValidators(maxValidators);
          control.get('max_extra_discount')?.updateValueAndValidity();
        }
      }
      else{
        const maxValidators =[
            Validators.min(minCurrentThreshold > 0 ? parseFloat(parseFloat(minCurrentThreshold + 0.01).toFixed(2)) : 0),
            Validators.max(100)
          ];
          if(this.negotiationFormArray.length > 1)
          {
            maxValidators.push(Validators.required)
          }
          control.get('max_extra_discount')?.setValidators(maxValidators);
          control.get('max_extra_discount')?.updateValueAndValidity();
          const minValidators =[
            Validators.required,
            Validators.min(0),
            Validators.max(100)
          ];
          control.get('min_extra_discount')?.setValidators(minValidators);
          control.get('min_extra_discount')?.updateValueAndValidity();
      }
    });
  }

  addNegotiation(min: number | null = null, max: number | null = null, new_commission: number = 0): void {
    const previousNegotiation = this.negotiationFormArray?.controls[this.negotiationFormArray.length - 1];
    const maxPreviousNegotiation = previousNegotiation?.get('max_extra_discount')?.value || 0;

    const negotiationFormGroup = new FormGroup(
      {
        min_extra_discount: new FormControl(min, [Validators.required, Validators.min(maxPreviousNegotiation > 0 ? maxPreviousNegotiation + 1 : 0)]),
        max_extra_discount: new FormControl(max, [
          Validators.required,
          Validators.min(maxPreviousNegotiation > 0 ? maxPreviousNegotiation + 1 : 0),
          Validators.max(100),
        ]),
        new_commission: new FormControl(new_commission, [Validators.required, Validators.min(0), Validators.max(100)]),
      },
      { validators: rangeValidator("min_extra_discount", "max_extra_discount") }
    );
    this.negotiationFormArray.push(negotiationFormGroup);
    this.updateNegotiationValidators()
    if (this.negotiationsArray) {
      this.negotiationsArray.nativeElement.scrollTop = this.negotiationsArray.nativeElement.scrollHeight;
    }
  }

  removeNegotiation(index: number): void {
    this.negotiationFormArray.removeAt(index);
  }

  onInputRemoveLeadingZero(event: Event, formGroup: any, formControlName: string) {
    const input = event.target as HTMLInputElement;
    if (/^0[0-9]+/.test(input.value)) {
      input.value = input.value.replace(/^0+/, '');
      formGroup.get(formControlName)?.setValue(input.value);
    }
  }

  next() {
    if (!this.negotiationForm.valid) {
      this.negotiationForm.markAllAsTouched();
      return;
    }

    const stepPayload = this.negotiationForm.getRawValue();
    console.log('Payload', stepPayload);
    this.nextStep.emit(stepPayload);
  }

  previous() {
    const stepPayload = this.negotiationForm.getRawValue()
    console.log('Payload', stepPayload);
    this.previousStep.emit(stepPayload);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
